import React from "react";
import { LayoutDashboard, Users, Car, Map, Calendar, BarChart3, Plug, Download, TrendingUp } from "lucide-react";
import PanelShell from "../../components/PanelShell";
import { useAuth } from "@/context/AuthContext";
import { kpis } from "../../lib/mockData";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, AreaChart, Area, RadialBarChart, RadialBar, Legend } from "recharts";

const navItems = [
  { to: "/admin", label: "Panel", icon: LayoutDashboard, end: true },
  { to: "/admin/reservas", label: "Reservas", icon: Calendar },
  { to: "/admin/conductores", label: "Conductores", icon: Car },
  { to: "/admin/rutas", label: "Rutas", icon: Map },
  { to: "/admin/usuarios", label: "Usuarios", icon: Users },
  { to: "/admin/integraciones", label: "Integraciones", icon: Plug },
  { to: "/admin/reportes", label: "Reportes", icon: BarChart3 },
];
const bottomNav = [
  { to: "/admin", label: "Panel", icon: LayoutDashboard, end: true },
  { to: "/admin/reservas", label: "Reservas", icon: Calendar },
  { to: "/admin/conductores", label: "Choferes", icon: Car },
  { to: "/admin/rutas", label: "Rutas", icon: Map },
  { to: "/admin/reportes", label: "Reportes", icon: BarChart3 },
];

const GOLD = "#C9A227";

const monthly = Array.from({ length: 12 }, (_, i) => ({
  m: ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"][i],
  trips: 180 + Math.round(Math.sin(i * 0.9) * 60) + i * 12,
  revenue: (180 + Math.round(Math.sin(i * 0.9) * 60) + i * 12) * 92,
}));

const AdminReports = () => {
  const { user } = useAuth();
  return (
    <PanelShell role="Admin" navItems={navItems} bottomNavItems={bottomNav} userName={user?.full_name} userMeta="Administrador" testId="admin-reports">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display">Analítica</p>
            <h1 className="font-display font-bold text-3xl uppercase tracking-tight mt-1">Reportes</h1>
          </div>
          <button className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10 font-display font-bold uppercase tracking-wider text-xs rounded-lg" data-testid="export-btn">
            <Download size={14} /> Exportar CSV
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-[#1C1D20] border border-[#2E2F33] rounded-xl p-6" data-testid="revenue-annual">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display">2025</p>
                <h3 className="font-display font-bold text-lg uppercase tracking-wide">Ingresos anuales</h3>
              </div>
              <p className="text-2xl font-display font-bold text-[#C9A227]">S/ 342,180</p>
            </div>
            <div className="h-72">
              <ResponsiveContainer>
                <AreaChart data={monthly}>
                  <defs>
                    <linearGradient id="gGold" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor={GOLD} stopOpacity={0.5} />
                      <stop offset="100%" stopColor={GOLD} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2E2F33" />
                  <XAxis dataKey="m" stroke="#A0A0A5" fontSize={11} tickLine={false} axisLine={{ stroke: "#2E2F33" }} />
                  <YAxis stroke="#A0A0A5" fontSize={11} tickLine={false} axisLine={{ stroke: "#2E2F33" }} />
                  <Tooltip contentStyle={{ backgroundColor: "#0D0E10", border: "1px solid #C9A227", borderRadius: 8, fontSize: 12 }} />
                  <Area type="monotone" dataKey="revenue" stroke={GOLD} strokeWidth={2} fill="url(#gGold)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-[#1C1D20] border border-[#2E2F33] rounded-xl p-6" data-testid="performance-card">
            <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display">Performance</p>
            <h3 className="font-display font-bold text-lg uppercase tracking-wide mb-6">Operacional</h3>
            <div className="space-y-4">
              {[
                { l: "Puntualidad", v: 98 },
                { l: "Satisfacción", v: 96 },
                { l: "Ocupación flota", v: 82 },
                { l: "Retención clientes", v: 91 },
              ].map((k) => (
                <div key={k.l}>
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="text-[#A0A0A5] uppercase tracking-wider font-display">{k.l}</span>
                    <span className="text-[#C9A227] font-mono">{k.v}%</span>
                  </div>
                  <div className="h-1.5 bg-[#0D0E10] rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#C9A227] to-[#D4AF37] rounded-full" style={{ width: `${k.v}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-4 bg-[#1C1D20] border border-[#2E2F33] rounded-xl p-6" data-testid="trips-monthly">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display">Volumen</p>
              <h3 className="font-display font-bold text-lg uppercase tracking-wide">Viajes por mes</h3>
            </div>
            <p className="text-2xl font-display font-bold text-[#F5F5F5]">3,724 <span className="text-sm text-[#A0A0A5] font-body">viajes</span></p>
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2E2F33" />
                <XAxis dataKey="m" stroke="#A0A0A5" fontSize={11} tickLine={false} axisLine={{ stroke: "#2E2F33" }} />
                <YAxis stroke="#A0A0A5" fontSize={11} tickLine={false} axisLine={{ stroke: "#2E2F33" }} />
                <Tooltip contentStyle={{ backgroundColor: "#0D0E10", border: "1px solid #C9A227", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="trips" fill={GOLD} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </PanelShell>
  );
};

export default AdminReports;
